<template>
  <div class="page-container">
    <h1 class="mb-4">
      <b>{{ port.portName }} ({{ port.countryCode }}) </b>| Port id:
      {{ getNested(portFormatted, "portId", "--") }}
    </h1>

    <!-- common port details -->
    <CRow>
      <CCol col="7" class="pr-3">
        <port-details-map
          :loading="loading"
          :port="port"
          :berths="berths.items"
          :shipyards="shipyards.items"
          :anchorages="anchorages.items"
          @refresh="refreshPortData"
          @edit-entity="handleEntityEdit(...arguments)"
        />
      </CCol>

      <CCol col="5">
        <div class="details">
          <div class="details__header">
            <div class="header-title">Port details</div>
            <div class="header-icon" @click="refreshPortData">
              <i
                class="fa fa-refresh refresh-icon"
                :class="{ 'fa-spin': loading }"
              ></i>
            </div>
          </div>

          <template v-if="portErrors.length">
            <CAlert
              v-for="(error, index) in portErrors"
              :key="`error-${index}`"
              color="danger"
              :show="10"
              closeButton
            >
              {{ error }}
            </CAlert>
          </template>

          <div class="details-row">
            <div class="details-row__title">Port Id</div>
            <div class="details-row__value">
              {{ getNested(portFormatted, "portId", "--") }}
            </div>
          </div>
          <div class="details-row">
            <div class="details-row__title">Port Name</div>
            <div class="details-row__value">
              <editable-field
                fieldType="text"
                fieldName="portName"
                :entityData="port"
                :saveMethod="updatePort"
                @updated="portUpdated"
                @update-error="portUpdateError"
                placeholder="--"
              />
            </div>
          </div>
          <div class="details-row">
            <div class="details-row__title">Code</div>
            <div class="details-row__value">
              <editable-field
                fieldType="text"
                fieldName="unlocode"
                :entityData="port"
                :saveMethod="updatePort"
                @updated="portUpdated"
                @update-error="portUpdateError"
                placeholder="--"
              />
            </div>
          </div>
          <div class="details-row">
            <div class="details-row__title">Region</div>
            <div class="details-row__value">
              <editable-field
                fieldType="select"
                fieldName="region"
                :items="regionChoices"
                :entityData="port"
                :saveMethod="updatePort"
                @updated="portUpdated"
                @update-error="portUpdateError"
                placeholder="--"
              />
            </div>
          </div>
          <div class="details-row">
            <div class="details-row__title">Sail Through</div>
            <div class="details-row__value">
              <editable-field
                fieldType="boolean"
                fieldName="sailThrough"
                :entityData="port"
                :saveMethod="updatePort"
                @updated="portUpdated"
                @update-error="portUpdateError"
                placeholder="--"
              />
            </div>
          </div>
          <div class="details-row">
            <div class="details-row__title">Updated at</div>
            <div class="details-row__value">{{ portFormatted.updatedAt }}</div>
          </div>
        </div>

        <div class="details">
          <div class="details__header">
            <div class="header-title">Notes</div>
          </div>

          <editable-field
            fieldType="textarea"
            fieldName="notes"
            :entityData="port"
            :saveMethod="updatePort"
            @updated="portUpdated"
            @update-error="portUpdateError"
            placeholder="- empty -"
          />
        </div>

        <div class="aliases">
          <alias-table
            title="Port name aliases"
            :port-id="portId"
            :list-method="listPortNameAliases"
            :add-method="addPortNameAlias"
            :remove-method="removePortNameAlias"
          />

          <alias-table
            title="Port unlocode aliases"
            :port-id="portId"
            :list-method="listPortUnlocodeAliases"
            :add-method="addPortUnlocodeAlias"
            :remove-method="removePortUnlocodeAlias"
          />
        </div>
      </CCol>
    </CRow>

    <!-- berths table -->
    <CRow class="mt-5">
      <CCol col="12" class="">
        <h3>Berths</h3>
        <port-table
          :loading="berths.loading"
          :items="berthsFormattedItems"
          :fields="berths.fields"
          :table-filter="false"
          @edit="handleEntityEdit($event, 'berth')"
          @remove="handleEntityRemove($event, 'berth')"
        >
        </port-table>
      </CCol>
    </CRow>

    <!-- anchorages table -->
    <CRow class="mt-5">
      <CCol col="12" class="">
        <h3>Anchorages</h3>
        <port-table
          :loading="anchorages.loading"
          :items="anchoragesFormattedItems"
          :fields="anchorages.fields"
          :table-filter="false"
          @edit="handleEntityEdit($event, 'anchorage')"
        >
        </port-table>
      </CCol>
    </CRow>

    <!-- shipyards table -->
    <CRow class="mt-5">
      <CCol col="12" class="">
        <h3>Shipyards</h3>
        <port-table
          :loading="shipyards.loading"
          :items="shipyardsFormattedItems"
          :fields="shipyards.fields"
          :table-filter="false"
          @edit="handleEntityEdit($event, 'shipyard')"
        >
        </port-table>
      </CCol>
    </CRow>

    <!-- modals -->
    <berth-edit-modal
      :berth="currentBerth"
      :show.sync="berths.showModal"
      @updated="entityUpdated('berth', $event)"
    />
    <anchorage-edit-modal
      :anchorage-id="anchorages.currentId"
      :show.sync="anchorages.showModal"
      @updated="entityUpdated('anchorage', $event)"
    />
    <shipyard-edit-modal
      :shipyard="currentShipyard"
      :show.sync="shipyards.showModal"
      @updated="entityUpdated('shipyard', $event)"
    />

    <remove-entity-modal
      :entity="remove.entity"
      :entity-type="remove.entityType"
      :show.sync="remove.showModal"
      @updated="refreshPortData"
    />
  </div>
</template>

<script>
import "leaflet/dist/leaflet.css";
import getNested from "lodash-es/get";
import cloneDeep from "lodash-es/cloneDeep";
import startCase from "lodash-es/startCase";

import moment from "moment";
import { mapActions } from "vuex";
import PortTable from "../components/PortTable";
import BerthEditModal from "../components/port/modals/BerthEditModal";
import AnchorageEditModal from "../components/port/modals/AnchorageEditModal";
import ShipyardEditModal from "../components/port/modals/ShipyardEditModal";
import RemoveEntityModal from "../components/port/modals/RemoveEntityModal";
import AliasTable from "../components/port/AliasTable";
import EditableField from "../components/widgets/EditableField";
import PortDetailsMap from "../components/port/PortDetailsMap";

import { regionList } from "../constants";

const maxInAreaEntitiesCount = 500;

export default {
  name: "port-details",

  components: {
    RemoveEntityModal,
    PortDetailsMap,
    EditableField,
    AliasTable,
    ShipyardEditModal,
    AnchorageEditModal,
    BerthEditModal,
    PortTable,
  },

  data() {
    // full featured port to test:
    // http://localhost:8080/#/ports/1566

    const editField = {
      key: "edit",
      label: "Edit",
      sorter: false,
      _classes: "text-center",
      filter: false,
    };

    const removeField = {
      key: "remove",
      label: "Remove",
      sorter: false,
      _classes: "text-center",
      filter: false,
    };

    return {
      loading: false,
      portId: this.getPortId(),
      port: {},
      portErrors: [],

      berths: {
        fields: [
          "berthName",
          "berthId",
          "berthType",
          "company",
          "updatedAt",
          editField,
          removeField,
        ],
        items: [],
        currentId: null,
        showModal: false,
        loading: false,
      },

      shipyards: {
        fields: ["shipyardName", "shipyardId", "updatedAt", editField],
        items: [],
        currentId: null,
        showModal: false,
        loading: false,
      },

      anchorages: {
        fields: [
          "anchorageName",
          "anchorageId",
          "anchorageType",
          "updatedAt",
          editField,
        ],
        items: [],
        currentId: null,
        showModal: false,
        loading: false,
      },

      remove: {
        entityType: null,
        entity: {},
        showModal: false,
      },
    };
  },

  async mounted() {
    await this.loadPortData();
  },

  methods: {
    ...mapActions("polygons", [
      "getPort",
      "updatePort",
      "getAnchorage",
      "addPortNameAlias",
      "addPortUnlocodeAlias",
      "removePortNameAlias",
      "removePortUnlocodeAlias",
      "listPortBerths",
      "listPortShipyards",
      "listPortAnchorages",
      "listPortNameAliases",
      "listPortUnlocodeAliases",
    ]),

    async refreshPortData() {
      this.portId = null;
      this.resetPortData();

      // next tick is required to force portId watchers (in alias tables)
      this.$nextTick(async () => {
        this.portId = this.getPortId();
        await this.loadPortData();
      });
    },

    async refreshSection(entityType) {
      await this[`load${startCase(entityType)}s`]();
    },

    async loadPortData() {
      this.loading = true;

      await this.loadPortDetails();
      // this.setMapCenter();
      await this.loadBerths();
      await this.loadShipyards();
      await this.loadAnchorages();
      // this.setMapBounds();

      this.loading = false;
    },

    resetPortData() {
      this.port = {};
      this.berths.items = [];
      this.berths.currentId = null;
      this.shipyards.items = [];
      this.shipyards.currentId = null;
      this.anchorages.items = [];
      this.anchorages.currentId = null;
      // this.rawInAreaZones = [];
    },

    getPortId() {
      let portId = getNested(this, "$route.params.port_id", null);
      if (portId) {
        return parseInt(portId);
      } else {
        return null;
      }
    },

    async loadPortDetails() {
      this.port = await this.getPort(this.portId);
    },

    async loadBerths() {
      this.berths.loading = true;
      this.berths.items = await this.listPortBerths(this.portId);
      this.berths.loading = false;
    },

    async loadShipyards() {
      this.shipyards.loading = true;
      this.shipyards.items = await this.listPortShipyards(this.portId);
      this.shipyards.loading = false;
    },

    async loadAnchorages() {
      this.anchorages.loading = true;
      let anchorages = await this.listPortAnchorages(this.portId);
      for (let item of anchorages) {
        let anchorage = await this.getAnchorage(item.anchorageId);
        this.anchorages.items.push(anchorage);
      }
      this.anchorages.loading = false;
    },

    portUpdated(data) {
      this.port = data;
    },

    portUpdateError(response) {
      this.portErrors = [];
      if (response.error_details) {
        this.portErrors.push(response.error_details);
      } else {
        this.portErrors.push("Unknown port update error");
      }
    },

    entityUpdated(entityType, data) {
      let section = this[`${entityType}s`];

      if (section.currentId) {
        const currentEntityIndex = section.items.findIndex(
          (item) => item[`${entityType}Id`] === section.currentId
        );

        if (typeof currentEntityIndex === "number") {
          this.$set(section.items, currentEntityIndex, data); // cause we need reactivity here
        }
      }
    },

    handleEntityEdit(item, entityType) {
      const id = item.id ? item.id : item[`${entityType}Id`];
      const sectionName = `${entityType}s`; // berths, anchorages, etc

      this[sectionName].currentId = id;
      this[sectionName].showModal = true;
    },

    handleEntityRemove(item, entityType) {
      this.remove.entity = item;
      this.remove.entityType = entityType;
      this.remove.showModal = true;
    },

    formatDateTime(timestamp) {
      if (timestamp) {
        return moment.utc(timestamp).format("YYYY-MM-DD HH:mm:ss");
      } else {
        return "--";
      }
    },

    getTableItems(items, fields) {
      if (!items.length) {
        return [];
      }

      return items.map((item) => {
        let formattedItem = {};
        fields.forEach((key) => {
          key = typeof key === "object" ? key.key : key;
          let value = item[key];

          if (key === "updatedAt") {
            formattedItem[key] = this.formatDateTime(value);
          } else {
            formattedItem[key] = value ? value : "--";
          }
        });

        formattedItem.portId = item.portId || null;
        return formattedItem;
      });
    },

    getNested: getNested,
  },

  computed: {
    portFormatted() {
      let port = cloneDeep(this.port);

      port.updatedAt = this.formatDateTime(port.updatedAt);
      if (typeof this.sailThrough === "boolean") {
        port.sailThrough = String(this.sailThrough);
      } else {
        port.sailThrough = "--";
      }

      return port;
    },

    regionChoices() {
      return regionList.map((item) => ({
        value: item.region_id,
        label: item.region_name,
      }));
    },

    currentBerth() {
      if (this.berths.currentId) {
        return this.berths.items.find((item) => {
          return this.berths.currentId === item.berthId;
        });
      } else {
        return null;
      }
    },

    currentShipyard() {
      if (this.shipyards.currentId) {
        return this.shipyards.items.find((item) => {
          return this.shipyards.currentId === item.shipyardId;
        });
      } else {
        return null;
      }
    },

    berthsFormattedItems() {
      return this.getTableItems(this.berths.items, this.berths.fields);
    },

    shipyardsFormattedItems() {
      return this.getTableItems(this.shipyards.items, this.shipyards.fields);
    },

    anchoragesFormattedItems() {
      return this.getTableItems(this.anchorages.items, this.anchorages.fields);
    },
  },

  watch: {
    "$route.params.port_id": async function() {
      let portId = this.getPortId();
      if (portId) {
        await this.refreshPortData();
      }
    },

    inAreaEntitiesCount(count) {
      const maxEntitiesErrorText =
        "Too many entities in view - zoom in to display all entities";

      if (
        count >= maxInAreaEntitiesCount &&
        !this.map.errors.includes(maxEntitiesErrorText)
      ) {
        this.map.errors.push(maxEntitiesErrorText);
      } else {
        this.map.errors = this.map.errors.filter(
          (errorText) => errorText !== maxEntitiesErrorText
        );
      }
    },
  },
};
</script>

<style scoped lang="scss">
.details {
  margin-bottom: 1rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.7rem;
    font-size: 18px;
    border-bottom: 1px solid #b2b2b2;

    .header-title {
      font-size: 16px;
    }

    .header-icon {
      cursor: pointer;
      font-size: 18px;
      color: #005800;
    }

    .header-icon:hover,
    .header-icon .fa-spin {
      color: #00a500;
    }
  }
}

.details-row {
  // padding-bottom: 5px;
  margin-bottom: 5px;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px dotted #d0d0d0;

  &__title {
    min-width: 100px;
    padding-right: 10px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__value {
    padding-left: 10px;
    font-weight: 500;
    font-size: 15px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    // direction: rtl;
    // text-align: left;
  }
}

.port-popup {
  font-size: 12px;

  .title {
    margin-bottom: 10px;
    font-size: 16px;
  }
}
</style>
